/**
 * Blog Index Page — Route Module
 *
 * Replaces app/[locale]/(marketing)/blog/page.tsx.
 */
import { routes } from '@letsgethome/routes';
import { isCategorySlug } from '@letsgethome/types/blog-categories';
import { PageContainer } from '@letsgethome/ui/page-container';
import { PaginatedDirectory } from '@letsgethome/ui/paginated-directory';
import { useLoaderData } from 'react-router';

import { DirectorySearch } from '../../../../shared/components/DirectorySearch';
import { SeoHead } from '../../../../shared/components/SeoHead';
import { Link } from '../../../../shared/i18n/routing';
import { useTranslations } from '../../../../shared/i18n/useTranslations';
import { PostCard } from '../components/PostCard';
import type { loader } from './blog.loader';

const chipClass = 'rounded-full border px-4 py-1.5 text-sm transition-colors';
const chipIdle = 'border-border text-muted-foreground hover:bg-muted';
const chipActive = 'border-primary bg-primary text-primary-foreground';

export function Component() {
  const { paginated, categories, query, seo } = useLoaderData<typeof loader>();
  const t = useTranslations('blog');

  return (
    <>
      <SeoHead {...seo} />
      <div className="min-h-screen">
        <PageContainer size="7xl" className="py-12 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold mb-8">{t('title')}</h1>
          <DirectorySearch
            action={routes.blog.index()}
            name="q"
            query={query}
            labels={{
              label: t('searchLabel'),
              placeholder: t('searchPlaceholder'),
              submit: t('searchSubmit'),
              clear: t('clearSearch'),
            }}
          />
          {categories.length > 0 && (
            <nav aria-label={t('categoriesLabel')} className="mb-8 flex flex-wrap gap-2">
              {/* On the blog index the whole corpus is shown, so "all
                  categories" is the current page; each category chip navigates
                  to that category's OWN canonical, indexable
                  /blog/category/{slug} page (SEO A3, ADR 0152) — the blog's
                  strongest internal-link nav points at indexable pages. */}
              <Link
                href={routes.blog.index()}
                className={`${chipClass} ${chipActive}`}
                aria-current="page"
              >
                {t('allCategories')}
              </Link>
              {categories.map((facet) => {
                // The facet slug is the closed-vocabulary kebab slug — the
                // server resolved each category's name and dropped any with no
                // page — so it narrows to a `CategorySlug` and links straight
                // to the canonical page.
                if (!isCategorySlug(facet.slug)) return null;
                return (
                  <Link
                    key={facet.slug}
                    href={routes.blog.category(facet.slug)}
                    className={`${chipClass} ${chipIdle}`}
                  >
                    {t('categoryChip', { name: facet.name, count: facet.count })}
                  </Link>
                );
              })}
            </nav>
          )}
          <PaginatedDirectory
            data={paginated}
            itemKey={(post) => post.id}
            renderItem={(post) => (
              <PostCard
                post={post}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              />
            )}
            hrefForPage={(n) =>
              routes.blog.index({
                page: n > 1 ? n : undefined,
                // Without this the pager silently drops the search on page 2.
                query: query || undefined,
              })
            }
            Link={Link}
            className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
            empty={
              <div className="text-center py-12">
                <p className="text-muted-foreground">
                  {query ? t('noPostsForSearch', { query }) : t('noPosts')}
                </p>
              </div>
            }
          />
        </PageContainer>
      </div>
    </>
  );
}
